<template>
  <div class="mt-3">
    <label for="price-priceType-select" class="form-label">
      {{ $t('service.priceType') }}
    </label>
    <TomSelect
      v-model="selectedType"
      :options="{
        create: false,
        placeholder: $t('service.priceTypeSelect')
      }"
      @change="$emit('update:price', getPriceData())"
      class="w-full"
    >
      <option
        v-for="priceType in priceTypes"
        :value="priceType.code"
        :key="priceType.code"
      >
        {{
          priceType.name['sk_SK'] === '' ? priceType.description : priceType.name['sk_SK']
        }}
      </option>
    </TomSelect>
  </div>
  <div class="mt-3" v-show="needAmount">
    <label for="price-priceUnit-select" class="form-label">
      {{ $t('service.priceUnit') }}
    </label>
    <TomSelect
      v-model="selectedUnit"
      :options="{
        create: false,
        placeholder: $t('service.priceUnitSelect')
      }"
      class="w-full"
      @change="$emit('update:price', getPriceData())"
    >
      <option
        v-for="priceUnit in priceUnits"
        :value="priceUnit.code"
        :key="priceUnit.code"
      >
        {{ priceUnit.name['sk_SK'] }}
      </option>
    </TomSelect>
  </div>
  <div class="mt-3" v-show="needAmount">
    <label for="amount" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('service.price') }}
    </label>
    <input
      id="amount"
      v-model.trim="validate.amount.$model"
      type="text"
      name="amount"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      :class="{ 'border-theme-6': validate.amount.$error }"
      placeholder=""
      @input="$emit('update:price', getPriceData())"
    />
  </div>
  <div class="mt-3" v-show="needAmount">
    <label for="currency" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('service.priceCurrency') }}
    </label>
    <input
      id="currency"
      v-model.trim="validate.currency.$model"
      type="text"
      name="currency"
      disabled="disabled"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      :class="{ 'border-theme-6': validate.currency.$error }"
      placeholder="EUR"
      @input="$emit('update:price', getPriceData())"
    />
  </div>
  <div class="mt-3" v-show="needUrl">
    <label for="priceUrl" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('common.priceUrl') }}
    </label>
    <input
      id="priceUrl"
      v-model.trim="validate.priceUrl.$model"
      type="text"
      name="priceUrl"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      :class="{ 'border-theme-6': validate.priceUrl.$error }"
      placeholder=""
      @input="$emit('update:price', getPriceData())"
    />
  </div>
  <div class="mt-3">
    <label class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('service.priceOnWeb') }}: {{ getPriceOnWeb() }}
    </label>
  </div>
</template>

<script>
import { defineComponent, toRefs, reactive, computed, ref } from 'vue'
import { helpers, url } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import serviceApi from '../../services/services/service.api'

export default defineComponent({
  data() {
    return {
      needAmount: true,
      needUrl: false,
      priceTypes: [],
      priceUnits: []
    }
  },

  mounted() {
    serviceApi.getPriceAttributeList().then(
      (response) => {
        this.priceTypes = response.data.types
        this.priceUnits = response.data.units
        if (this.formData.selectedType !== '') {
          this.selectedType = this.formData.selectedType
          this.selectedUnit =
            this.formData.selectedUnit === null ? '' : this.formData.selectedUnit
          this.checkNeedAmount()
          this.checkNeedUrl()
          return
        }
        if (this.priceTypes.length > 0) {
          this.selectedType = this.priceTypes[0].code
        }
        if (this.priceUnits.length > 0) {
          this.selectedUnit = this.priceUnits[0].code
        }
        this.checkNeedAmount()
        this.checkNeedUrl()
      },
      (error) => {
        console.log(error)
      }
    )
  },

  props: {
    price: {
      type: Object,
      default: () => {
        return {
          selectedType: '',
          selectedUnit: '',
          amount: '',
          currency: 'EUR',
          priceUrl: ''
        }
      }
    }
  },

  methods: {
    getPriceOnWeb() {
      const priceData = this.getPriceData()
      const priceType = this.priceTypes.find((x) => x.code === priceData.selectedType)
      if (priceType === undefined || priceType === '') {
        return ''
      }
      let priceText = priceType.name.sk_SK
      if (priceType.needAmount) {
        priceText += ' ' + priceData.amount
        priceText += ' €'
        priceText +=
          ' ' + this.priceUnits.find((x) => x.code === priceData.selectedUnit).name.sk_SK
      }

      return priceText
    },
    checkNeedAmount() {
      const selectedPriceType = this.priceTypes.find(
        (priceType) => priceType.code === this.selectedType
      )
      const newValue = selectedPriceType ? selectedPriceType.needAmount : true
      if (!this.needAmount && newValue) {
        this.selectedUnit = this.priceUnits[0].code
      }
      this.needAmount = newValue
    },
    checkNeedUrl() {
      const selectedPriceType = this.priceTypes.find(
        (priceType) => priceType.code === this.selectedType
      )
      this.needUrl = selectedPriceType ? selectedPriceType.needUrl : true
    },
    getPriceData() {
      this.checkNeedAmount()
      this.checkNeedUrl()
      if (this.needAmount) {
        return {
          selectedType: this.selectedType,
          selectedUnit: this.selectedUnit,
          amount: this.formData.amount,
          currency: 'EUR',
          priceUrl: null
        }
      }
      if (this.needUrl) {
        return {
          selectedType: this.selectedType,
          selectedUnit: null,
          amount: null,
          currency: null,
          priceUrl: this.formData.priceUrl
        }
      }
      return {
        selectedType: this.selectedType,
        selectedUnit: null,
        amount: null,
        currency: null,
        priceUrl: null
      }
    },
    setPriceData(data) {
      this.selectedType = data.selectedType
      this.selectedUnit = data.selectedUnit
      this.formData.amount = data.amount
      this.formData.currency = data.currency
      this.formData.priceUrl = data.priceUrl
    }
  },

  emits: ['update:price'],

  setup(props, { emit }) {
    const amountValidator = helpers.regex(/^[0-9]*[,.]?[0-9]*$/)
    const currencyValidator = helpers.regex(/^EUR$/)
    const selectedType = ref('')
    const selectedUnit = ref('')
    const inputData = ref({
      data: props.price
    })
    const formData = reactive({
      selectedType: props.price.selectedType,
      selectedUnit: props.price.selectedUnit,
      amount: props.price.amount,
      currency: props.price.currency,
      priceUrl: props.price.priceUrl
    })
    const rules = {
      amount: {
        amountValidator
      },
      currency: {
        currencyValidator
      },
      priceUrl: {
        url: helpers.withMessage('form.validation.url', url)
      }
    }
    const validate = useVuelidate(rules, toRefs(formData))

    const componentValue = computed({
      get: () => props.price,
      set: () => emit('update:price', validate)
    })

    return {
      inputData,
      validate,
      formData,
      componentValue,
      selectedType,
      selectedUnit
    }
  }
})
</script>
